import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  GlobalBannerCta,
  GlobalBannerMessage,
  GlobalBannerMessageRaw,
  GlobalBannerMessageType,
  GlobalBannerResponse
} from '../../models/global-banner-interface';
import { ApiService } from './api.service';
import { SupportedLang } from '../config/localized-paths';

const SUPPORTED_MESSAGE_TYPES: GlobalBannerMessageType[] = ['Informational', 'Warning', 'Alert'];

@Injectable({
  providedIn: 'root'
})
export class GlobalBannerService {
  constructor(private readonly apiService: ApiService) {}

  /**
   * Returns the list of active global banner messages fetched from the AEM
   * middleware endpoint (`api/global-banner-message/all-messages?locale=`).
   *
   * The mapping is resilient to both the flat array returned by the backend
   * gateway and the raw wrapped AEM GraphQL shape.
   * @param locale - AEM returns banner content in this language.
   */
  getGlobalBannerMessages(locale: SupportedLang = 'en'): Observable<GlobalBannerMessage[]> {
    return this.apiService.getGlobalBannerMessages(locale).pipe(
      map((response: GlobalBannerResponse | GlobalBannerMessageRaw[]) =>
        this.mapResponse(response)
      ),
      catchError(() => of<GlobalBannerMessage[]>([]))
    );
  }

  private mapResponse(
    response: GlobalBannerResponse | GlobalBannerMessageRaw[]
  ): GlobalBannerMessage[] {
    const items: GlobalBannerMessageRaw[] = Array.isArray(response)
      ? response
      : response?.data?.myceGlobalBannerMessageList?.items ?? [];
    return items.map((item, index) => this.mapMessage(item, index));
  }

  private mapMessage(item: GlobalBannerMessageRaw, index: number): GlobalBannerMessage {
    return {
      id: this.buildId(item._path, index),
      path: item._path ?? '',
      messageType: this.resolveMessageType(item.messageTypes),
      messageCatch: (item.messageCatch ?? '').trim(),
      messageTitle: (item.messageTitle ?? '').trim(),
      messageDescription: (item.messageDescription?.html ?? '').trim(),
      dismissible: !!item.dismissibleFlag,
      cta: this.buildCta(item.ctaLabel, item.ctaLink),
      pages: item.pages,
      users: item.users,
      expanded: false,
      dismissed: false
    };
  }

  private buildId(path: string | undefined, index: number): string {
    const slug = (path ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    return `global-banner-${index}${slug ? '-' + slug : ''}`;
  }

  private resolveMessageType(rawType: string): GlobalBannerMessageType {
    const match = SUPPORTED_MESSAGE_TYPES.find(
      (type) => type.toLowerCase() === (rawType ?? '').trim().toLowerCase()
    );
    return match ?? 'Informational';
  }

  /**
   * Build a structured CTA from the AEM `ctaLabel`/`ctaLink` fields.
   * Returns `undefined` when either value is missing (no CTA configured).
   */
  private buildCta(
    ctaLabel?: string | null,
    ctaLink?: string | null
  ): GlobalBannerCta | undefined {
    const label = ctaLabel?.trim();
    const link = ctaLink?.trim();
    if (label && link) {
      return { ctaLabel: label, ctaLink: link };
    }
    return undefined;
  }
}
